import { MouseEvent, MutableRefObject } from 'react'
import { cn } from '../../lib/utils'

interface MoreKeysPopoverProps {
  open: boolean
  onClose: () => void
  onSendKey: (data: string) => void
  /**
   * Mutable ref shared with the parent bar — kept for API stability and
   * potential future paste-aware actions. Currently unused since Clear sends
   * Ctrl-U (kill line) which clears the entire input regardless of paste.
   */
  lastPastedRef?: MutableRefObject<string>
}

interface PopoverKey {
  label: string
  data: string
  action?: 'paste' | 'clear-line'
}

interface KeyGroup {
  title: string
  keys: PopoverKey[]
}

// Clear strategy: send Ctrl-U (kills current line in shells, instant) PLUS a
// burst of DEL bytes (\x7f) for multi-line TUI text inputs (claude code,
// ink-based REPLs, vim insert mode, chat composers) where Ctrl-U only clears
// one logical line. Burst was 1000 originally; ink-based composers re-render
// per keystroke and a thousand-byte burst could briefly stall the TUI on
// slower workspaces. 100 covers practical inputs and trailing DELs no-op once
// empty, so over-counting is harmless.
const CLEAR_LINE_PREFIX = '\x15'
const CLEAR_DEL_COUNT = 100
const CLEAR_DEL_BURST = '\x7f'.repeat(CLEAR_DEL_COUNT)
const CLEAR_INPUT = CLEAR_LINE_PREFIX + CLEAR_DEL_BURST

const KEY_GROUPS: KeyGroup[] = [
  {
    title: 'Clipboard',
    keys: [
      { label: 'Paste', data: '', action: 'paste' },
      { label: 'Clear', data: '', action: 'clear-line' },
    ],
  },
  {
    title: 'Editing',
    keys: [
      { label: 'Shift+Tab', data: '\x1b[Z' },
      { label: 'Ctrl+W', data: '\x17' },
      { label: 'Ctrl+U', data: '\x15' },
    ],
  },
  {
    title: 'Navigation',
    keys: [
      { label: 'Ctrl+A', data: '\x01' },
      { label: 'Ctrl+E', data: '\x05' },
    ],
  },
  {
    title: 'Process Control',
    keys: [
      { label: 'Ctrl+D', data: '\x04' },
      { label: 'Ctrl+L', data: '\x0c' },
      { label: 'Ctrl+Z', data: '\x1a' },
    ],
  },
]

/**
 * Overflow popover for the mobile keyboard bar.
 *
 * Positioned ABSOLUTELY above the parent bar (`bottom: 100%`) — works because
 * the parent wrapper is `position: relative`. No fixed-position math, so it
 * follows the bar across iOS versions / orientation changes / keyboard
 * toggles automatically.
 *
 * Special actions:
 *   - 'paste': reads navigator.clipboard.readText() and forwards as terminal
 *     input. Stores text in lastPastedRef for parity with older callers.
 *   - 'clear-line': sends Ctrl-U, which clears the entire current input
 *     across shells, claude code, and most TUI text inputs. (Was previously
 *     "send N backspaces equal to last paste length" but that only undid the
 *     paste; users want everything in the input cleared.)
 */
export function MoreKeysPopover({
  open,
  onClose,
  onSendKey,
  lastPastedRef,
}: MoreKeysPopoverProps) {
  if (!open) return null

  const preventBlur = (e: MouseEvent) => e.preventDefault()

  const handleKey = async (key: PopoverKey) => {
    if (key.action === 'paste') {
      try {
        const text = await navigator.clipboard.readText()
        if (text) {
          if (lastPastedRef) lastPastedRef.current = text
          onSendKey(text)
        }
      } catch {
        // Permission denied or clipboard empty — silently no-op.
      }
      onClose()
      return
    }
    if (key.action === 'clear-line') {
      onSendKey(CLEAR_INPUT)
      if (lastPastedRef) lastPastedRef.current = ''
      onClose()
      return
    }
    onSendKey(key.data)
    onClose()
  }

  return (
    <>
      {/* Backdrop is screen-fixed so it covers everything below the panel. */}
      <div
        data-testid="more-keys-backdrop"
        className="fixed inset-0 z-40"
        onMouseDown={preventBlur}
        onClick={onClose}
      />
      {/* Panel is absolutely positioned above its parent bar — `bottom: 100%`
          + `mb-1` puts it just above. Width clamps to viewport on small
          screens. Anchored to the bar (not the viewport), so it follows
          correctly regardless of keyboard state. */}
      <div
        className="absolute bottom-full left-1/2 -translate-x-1/2 z-50 mb-1 w-[min(360px,calc(100vw-16px))] max-h-[60vh] overflow-y-auto rounded-lg border border-border bg-card shadow-lg p-3 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        {KEY_GROUPS.map((group) => (
          <div key={group.title}>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {group.title}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {group.keys.map((key) => (
                <button
                  key={key.label}
                  type="button"
                  onMouseDown={preventBlur}
                  onClick={() => { void handleKey(key) }}
                  className={cn(
                    'px-3 py-2 min-w-[44px] min-h-[44px] rounded-md text-sm font-medium',
                    'flex items-center justify-center',
                    'bg-secondary hover:bg-secondary/80 text-secondary-foreground transition-colors',
                  )}
                >
                  {key.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
